"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import toolsData from "@/data/tools.json";
import shuffleArray from "@/utils/random";
import { techIconNeedsLightBackground } from "@/utils/techIconLightBg";
import { HORIZONTAL_LOOP_ITEM_CLASS, horizontalLoop } from "@/utils/horizontalLoop";

type Tool = {
  name: string;
  icon: string;
  link: string;
};

const LOOP_SPEED = 0.4;
const loopItemSelector = `.${HORIZONTAL_LOOP_ITEM_CLASS}`;

function ToolCell({ tool }: { tool: Tool }) {
  const tile = (
    <div className="aspect-square bg-surface-container-high p-2 flex flex-col justify-center items-center gap-2 rounded-lg w-20 h-20 border border-white/5">
      {tool.icon ? (
        techIconNeedsLightBackground(tool.icon) ? (
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-white p-1">
            {/* eslint-disable-next-line @next/next/no-img-element -- URLs locales dans tools.json */}
            <img
              src={tool.icon}
              alt={`Logo ${tool.name}`}
              width={32}
              height={32}
              className="max-h-full max-w-full object-contain"
            />
          </span>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element -- URLs locales dans tools.json
          <img src={tool.icon} alt={`Logo ${tool.name}`} width={32} height={32} className="size-8 object-contain" />
        )
      ) : (
        <span className="text-xs font-mono font-semibold text-primary tabular-nums">
          {tool.name.slice(0, 2).toUpperCase()}
        </span>
      )}
      <p className="font-headline text-sm text-center leading-tight line-clamp-2">{tool.name}</p>
    </div>
  );

  if (tool.link) {
    return (
      <a
        href={tool.link}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {tile}
      </a>
    );
  }

  return tile;
}

export default function ToolsCard({ className }: { className?: string }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [tools, setTools] = useState<Tool[]>(() => [...(toolsData as Tool[])]);

  useLayoutEffect(() => {
    setTools((prev) => {
      const next = [...prev];
      shuffleArray(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const items = Array.from(row.querySelectorAll<HTMLElement>(loopItemSelector));
    if (!items.length) return;

    const tl = horizontalLoop(items, {
      speed: LOOP_SPEED,
      reversed: false,
      repeat: -1,
    });

    const onEnter = () => gsap.to(tl, { timeScale: 0, overwrite: true });
    const onLeave = () => gsap.to(tl, { timeScale: 1, overwrite: true });
    items.forEach((item) => {
      item.addEventListener("mouseenter", onEnter);
      item.addEventListener("mouseleave", onLeave);
    });

    return () => {
      items.forEach((item) => {
        item.removeEventListener("mouseenter", onEnter);
        item.removeEventListener("mouseleave", onLeave);
      });
      tl.kill();
    };
  }, [tools]);

  return (
    <div className={`card py-6 ${className}`}>
      <div className="space-y-2 px-6 pb-4 text-center">
        <h3 className="text-2xl font-headline font-bold">Mes Outils de Travail</h3>
        <p className="text-white/70">
          J&apos;utilise ces outils de travail quotidiennement dans mes projets.
        </p>
      </div>
      <div className="overflow-hidden">
        <div ref={rowRef} className="flex gap-3">
          {Array.from({ length: 3 }).flatMap((_, dupIndex) =>
            tools.map((tool) => (
              <div
                key={`${tool.name}-${dupIndex}`}
                className={`${HORIZONTAL_LOOP_ITEM_CLASS} shrink-0`}
              >
                <ToolCell tool={tool} />
              </div>
            )),
          )}
        </div>
      </div>
    </div>
  );
}
